import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import StreakTapToContinue from '../../streakbattleroyale/components/StreakTapToContinue';
import i18n from 'src/lib/i18n/i18n';
import PopupBasic from '../../components/popups/PopupBasic';
import { AssetGroup } from '@play-co/timestep-core';
import StateObserver from 'src/StateObserver';
import {
  FlexBoxImageScaleView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import ShineFx from 'src/game/minigame/match2-timestep/components/ShineFx';
import { ImageView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { animateGems } from 'src/game/animation/CurrencyAnimation';
import { getHomeShortcutGemsReward } from '../homeShortcut.utils';

const assets = {
  addEn: 'assets/ui/homeShortcut/popups/reward/title-success-en.png',
  addJa: 'assets/ui/homeShortcut/popups/reward/title-success-ja.png',
  launchEn: 'assets/ui/homeShortcut/popups/reward/title-congratulations-en.png',
  launchJa: 'assets/ui/homeShortcut/popups/reward/title-congratulations-ja.png',
  gem: 'assets/ui/homeShortcut/popups/reward/icon-gem.png',
};

export default class PopupHomeShortcutReward extends PopupBasic {
  private close: VoidFunction;

  protected getAssetsGroup() {
    return new AssetGroup(Object.values(assets));
  }

  constructor(opts: { superview: View; close: VoidFunction }) {
    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width: 720,
      height: 1280,
      close: () => {
        sounds.sfx.play('ok');
        opts.close();
      },
      zIndex: 999,
      tracking: {
        feature: Feature.HOME_SHORTCUT._,
        $subFeature: Feature.HOME_SHORTCUT.REWARD_POPUP._,
      },
    });

    this.close = opts.close;

    // Remove the default close button
    this.buttonClose.removeFromSuperview();

    // Adjust background color and opacity
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });
  }

  public override init(opts: { type: 'add' | 'launch' }) {
    super.init({});

    const { type } = opts;

    // Create header wrapper container
    const container = new FlexBoxView({
      superview: this.box,
      y: 175,
      flex: {
        flexDirection: 'column',
        width: this.box.style.width,
        alignItems: 'center',
      },
    });

    // Create title image
    const isEn = StateObserver.getLocale() === 'en';
    new FlexBoxImageScaleView({
      superview: container,
      image: isEn ? assets[`${type}En`] : assets[`${type}Ja`],
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      flex: {
        width: 502,
        height: 100,
        marginRight: 25,
        marginBottom: 5,
      },
    });

    // Create message label
    new LangBitmapFontTextShadowView({
      superview: container,
      width: 445,
      height: 76,
      align: 'center',
      font: 'Body',
      size: 32,
      color: '#FFEA7B',
      textShadowOffsetX: 0,
      textShadowOffsetY: 6,
      textShadowColor: 'rgba(0,0,0,0.25)',
      localeText: () =>
        i18n(`homeShortcut.popup.reward.message${type === 'add' ? 1 : 2}`),
      wordWrap: true,
    });

    // Create Shine VFX
    const shine = new ShineFx({
      superview: this.box,
      scale: 0.85,
      opacity: 0.75,
    });
    shine.updateOpts({
      x: this.box.style.width * 0.5,
      y: this.box.style.height * 0.5,
    });
    shine.show();

    // Create gem icon
    const icon = new ImageView({
      superview: this.box,
      image: assets.gem,
      width: 250,
      height: 216,
      centerOnOrigin: true,
      centerAnchor: true,
      x: shine.style.x,
      y: shine.style.y,
    });

    // Create gem label
    new LangBitmapFontTextView({
      superview: icon,
      width: 170,
      height: 90,
      centerOnOrigin: true,
      x: icon.style.width * 0.5 - 20,
      y: icon.style.height - 25,
      align: 'center',
      verticalAlign: 'center',
      size: 72,
      font: 'Title',
      localeText: () => `+${getHomeShortcutGemsReward(opts.type)}`,
    });

    // 'Tap to continue' CTA
    new StreakTapToContinue({
      superview: this.box,
      offsetY: -75,
      textOpts: {
        size: isEn ? 46 : 35,
        color: '#FFE380',
        textShadowOpacity: 1,
        textShadowColor: '#613428',
        textShadowOffsetY: 8,
      },
      tracking: {
        feature: Feature.HOME_SHORTCUT.REWARD_POPUP._,
        $subFeature: Feature.HOME_SHORTCUT.REWARD_POPUP.CONTINUE,
      },
      onClick: async () => {
        // Animate gems flying to the gems wallet
        await animateGems({
          superview: this.box,
          start: {
            position: { x: icon.style.x, y: icon.style.y },
            scale: 2,
          },
          count: getHomeShortcutGemsReward(opts.type),
        });

        this.close();
      },
    });
  }
}
